import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  IconButton, Tooltip,
} from '@material-ui/core';
import { Formik, FieldArray } from 'formik';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import * as Yup from 'yup';
import styles from '../PAConfig.module.scss';
import { CheckAtLevel, FieldCategory, PACustomFieldOption, DrlsApiOperation } from '../types';
import { useStoreActions } from 'easy-peasy';
import { setValidationMessage, MESSAGE } from '../../../submodule';
interface AddEditPAConfigProps {
  openAddPAConfig: boolean;
  setOpenAddPAConfig: React.Dispatch<React.SetStateAction<boolean>>;
  editRowData?: Partial<PACustomFieldOption>;
  orgId: string;
}

const FIELD_CATEGORY_OPTIONS: { label: string; value: FieldCategory }[] = [
  { label: 'Standard', value: 'standard' },
  { label: 'Checkpoint', value: 'checkpoint' },
  { label: 'Static', value: 'static' },
];

const FIELD_CHECKATLEVEL_OPTIONS: { label: string; value: CheckAtLevel }[] = [
  { label: 'LineItem', value: 'LineItem' },
  { label: 'Request', value: 'Request' },
];

const AddEditPAConfig: React.FC<AddEditPAConfigProps> = ({
  openAddPAConfig,
  setOpenAddPAConfig,
  editRowData,
  orgId,
}) => {
  const commonAction = useStoreActions((actions: any) => actions.common);
  const settingsAction = useStoreActions((actions: any) => actions.settings);

  const schema = Yup.object({
    field_key: Yup.string().required(setValidationMessage(MESSAGE.VALIDATION.REQUIRED, {'{name}': 'Field Key'})),
    field_label: Yup.string().required(setValidationMessage(MESSAGE.VALIDATION.REQUIRED, {'{name}': 'Field Label'})),
    field_category: Yup.array().min(1, 'Select at least one category'),
    check_at_level: Yup.array().min(1, 'Select at least one level'),
    fhir_path: Yup.array().of(
      Yup.object({
        path: Yup.string().when('drls_apis', {
          is: (drls_apis: any[]) => drls_apis && drls_apis.some(api => api.type),
          then: Yup.string()
            .required('Path is required')
            .test('is-json', 'Invalid JSON format', (value) => {
              try {
                JSON.parse(value!);
                return true;
              } catch {
                return false;
              }
            }),
          otherwise: Yup.string().notRequired(),
        }),
        drls_apis: Yup.array().of(
          Yup.object({
            type: Yup.string().notRequired(),
            sub_type: Yup.array().when('type', {
              is: (type: string) => !!type,
              then: Yup.array().of(Yup.string().required()).min(1, 'Subtype is required'),
              otherwise: Yup.array().of(Yup.string()).notRequired(),
            }),
          })
        ).min(0),
      })
    ).min(0),
  });

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  const formatBackendErrors = (errorList: Record<string, string>[] = []) => {
    const outputErrors: Record<string, any> = {};

    errorList.forEach((item) => {
      const key = Object.keys(item)[0];
      const value = item[key];

      const normalizedKey = key.replace(
        /(fhir_path\[\d+\]\.path)\..+/,
        '$1'
      );

      const keys = normalizedKey.replace(/\[(\d+)\]/g, '.$1').split('.');

      let current = outputErrors;

      keys.forEach((k, index) => {
        const isLast = index === keys.length - 1;
        const nextKey = keys[index + 1];

        if (isLast) {
          current[k] = value;
        } else {
          if (!current[k]) {
            current[k] = /^\d+$/.test(nextKey) ? [] : {};
          }
          current = current[k];
        }
      });
    });

    return outputErrors;
  };

  const onSubmit = async (formData: PACustomFieldOption, { setErrors }) => {
    const formattedValues = {
      ...formData,
      fhir_path: formData?.fhir_path?.filter((i: any) => i?.drls_apis?.some((api: any) => api?.type?.trim()))?.map((item) => ({
        ...item,
        path: item.drls_apis && item.drls_apis.length > 0 && item.drls_apis[0].type ? typeof item?.path === 'string'
          ? JSON.parse(item?.path)
          : item?.path
        : '',
      }))
    };

    setSubmitLoader(true);
    if(formData && formData.PK && formData.SK) {
      settingsAction.updatePaCustomField(formattedValues)
        .then((res: object) => {
          setOpenAddPAConfig(false);
          commonAction.setSubmitSnackbar({
            status: true,
            messages: `Record updated successfully`,
          });
        })
        .catch((error: any) => {
          if (error.response && error.response.status === 422) {
            const errors = formatBackendErrors(error?.response?.data?.error);
            setErrors(errors);
          } else {
            commonAction.setSubmitSnackbar({
              status: true,
              messages: `Something went wrong while updating record. Please try again.`,
              error: true,
            });
          }
        })
        .finally(()=>{
          setSubmitLoader(false);
        });
    } else {
      settingsAction.savePaCustomField(formattedValues)
        .then((res: object) => {
          setOpenAddPAConfig(false);
          commonAction.setSubmitSnackbar({
            status: true,
            messages: `Record added successfully`,
          });
        })
        .catch((error: any) => {
          if (error.response && error.response.status === 422) {
            const errors = formatBackendErrors(error?.response?.data?.error);
            setErrors(errors);
          } else {
            commonAction.setSubmitSnackbar({
              status: true,
              messages: `Something went wrong while adding record. Please try again.`,
              error: true,
            });
          }
        })
        .finally(()=>{
          setSubmitLoader(false);
        });
    }
  };

  return (
    <Dialog
      open={openAddPAConfig}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 20 } }}
    >
      <Formik<PACustomFieldOption>
        initialValues={{
          PK: editRowData?.PK || '',
          SK: editRowData?.SK || '',
          field_key: editRowData?.field_key || '',
          fhir_path: editRowData?.fhir_path && editRowData?.fhir_path.length > 0
            ? editRowData.fhir_path.map((item: any) => ({
                ...item,
                path: typeof item.path === 'string' ? item.path : JSON.stringify(item.path, null, 2),
              }))
            : [
                {
                  path: '',
                  drls_apis: [
                    {
                      type: '',
                      sub_type: [],
                    },
                  ],
                },
              ],
          check_at_level: editRowData?.check_at_level || [],
          field_label: editRowData?.field_label || '',
          field_category: editRowData?.field_category || [],
          orgs_id: editRowData?.orgs_id || orgId,
          entity_type: editRowData?.entity_type || '',
          create_date: editRowData?.create_date || new Date().toISOString(),
          modify_date: editRowData?.modify_date || new Date().toISOString(),
          created_by: editRowData?.created_by || '',
          modified_by: editRowData?.modified_by || '',
        }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {({
          values,
          touched,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => {
          return (
            <Box className={styles.dialogmainwrapper}>
              <form onSubmit={handleSubmit} noValidate>
                <DialogContent>
                  <Grid container spacing={2}>
                    <Grid item md={12} xs={12}>
                      <Typography variant="h6">PA Config</Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextField
                        required
                        label="Field Key"
                        name="field_key"
                        value={values.field_key}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        variant="outlined"
                        error={Boolean(touched.field_key && errors.field_key)}
                        helperText={
                          touched.field_key && errors.field_key
                            ? errors.field_key
                            : ''
                        }
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        required
                        label="Field Label"
                        name="field_label"
                        value={values.field_label}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        variant="outlined"
                        error={Boolean(touched.field_label && errors.field_label)}
                        helperText={
                          touched.field_label && errors.field_label
                            ? errors.field_label
                            : ''
                        }
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormControl fullWidth variant="outlined" required
                        error={touched.field_category && Boolean(errors.field_category)}
                      >
                        <InputLabel id="field-category-label">Field Category</InputLabel>
                        <Select
                          required
                          labelId="field-category-label"
                          multiple
                          name="field_category"
                          value={values.field_category}
                          onChange={(e) => {
                            const value = e.target.value as FieldCategory[];
                            setFieldValue('field_category', value);
                          }}
                          onBlur={handleBlur}
                          label="Field Category"
                          renderValue={(selected) =>
                            (selected as string[])
                              .map(
                                (item) => item.charAt(0).toUpperCase() + item.slice(1)
                              )
                              .join(', ')
                          }
                          error={Boolean(touched.field_category && errors.field_category)}
                        >
                          {FIELD_CATEGORY_OPTIONS?.filter((data) => data.value !== 'standard')?.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={values.field_category.includes(option.value)} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>

                        {touched.field_category && errors.field_category && (
                          <FormHelperText
                            className={
                              errors.field_category &&
                              touched.field_category &&
                              errors.field_category
                                ? 'Mui-error'
                                : ''
                            }
                          >
                            {errors.field_category &&
                              touched.field_category &&
                              errors.field_category}
                          </FormHelperText>
                        )}
                        </FormControl>
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <FormControl fullWidth variant="outlined" required
                        error={touched.check_at_level && Boolean(errors.check_at_level)}
                      >
                        <InputLabel id="field-checkatlevel-label">Check at Level</InputLabel>
                        <Select
                          required
                          labelId="field-checkatlevel-label"
                          multiple
                          name="check_at_level"
                          value={values.check_at_level}
                          onChange={(e) => {
                            const value = e.target.value as FieldCategory[];
                            setFieldValue('check_at_level', value);
                          }}
                          onBlur={handleBlur}
                          label="Field Category"
                          renderValue={(selected) => (selected as string[]).join(', ')}
                          error={Boolean(touched.check_at_level && errors.check_at_level)}
                        >
                          {FIELD_CHECKATLEVEL_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={values.check_at_level.includes(option.value)} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>

                        {touched.check_at_level && errors.check_at_level && (
                          <FormHelperText
                            className={
                              errors.check_at_level &&
                              touched.check_at_level &&
                              errors.check_at_level
                                ? 'Mui-error'
                                : ''
                            }
                          >
                            {errors.check_at_level &&
                              touched.check_at_level &&
                              errors.check_at_level}
                          </FormHelperText>
                        )}
                        </FormControl>
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <Typography style={{ marginTop: 8, fontWeight: 'bold', fontSize: '15px' }}>Select Config Type</Typography>
                      <FieldArray name="fhir_path">
                        {({ push, remove }) => (
                          <>
                            {values.fhir_path.map((data, index) => (
                              <Grid container spacing={2} key={index} style={{ marginTop: 8 }}>
                                {index !== 0 ? (<hr 
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    height: '2px',
                                    background: 'linear-gradient(to right, transparent, #d1d5db, transparent)',
                                    margin: '15px 0',
                                  }}
                                />) : null}
                                <Grid item md={5} xs={12} style={{ display: 'flex', flexDirection: 'column' }}>
                                  {data?.drls_apis.map((innerData, i) => {
                                    const selectedType = values.fhir_path[index].drls_apis[i].type;
                                    const subTypeOptions = DrlsApiOperation[selectedType] || [];
                                    return (
                                      <React.Fragment key={i}>
                                        {/* TYPE */}
                                        <Grid item md={12} xs={12}>
                                          <FormControl fullWidth variant="outlined"
                                            error={
                                              touched.fhir_path?.[index]?.drls_apis?.[i]?.type &&
                                              Boolean(
                                                (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type
                                              )
                                            }
                                          >
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                              name={`fhir_path[${index}].drls_apis[${i}].type`}
                                              value={values.fhir_path[index].drls_apis[i].type}
                                              onChange={(e) => {
                                                const type = e.target.value;
                                                setFieldValue(`fhir_path[${index}].drls_apis[${i}].type`, type);
                                                setFieldValue(`fhir_path[${index}].drls_apis[${i}].sub_type`, []);
                                              }}
                                              label="Type"
                                            >
                                              {Object.keys(DrlsApiOperation).map((item) => (
                                                <MenuItem key={item} value={item}>
                                                  {item}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                            {touched.fhir_path?.[index]?.drls_apis?.[i]?.type && Boolean(
                                              (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type
                                              ) && (
                                              <FormHelperText
                                                className={
                                                  Boolean(
                                                    (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type
                                                  ) &&
                                                  touched.fhir_path?.[index]?.drls_apis?.[i]?.type &&
                                                  Boolean(
                                                    (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type
                                                  )
                                                    ? 'Mui-error'
                                                    : ''
                                                }
                                              >
                                                {(errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type &&
                                                  touched.fhir_path?.[index]?.drls_apis?.[i]?.type &&
                                                  (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.type
                                                }
                                              </FormHelperText>
                                            )}
                                          </FormControl>
                                        </Grid>
                                        {/* SUBTYPE */}
                                        <Grid item md={12} xs={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                                          <FormControl fullWidth variant="outlined" 
                                            required={values?.fhir_path[index]?.drls_apis[i]?.type ? true : false}
                                            error={
                                              touched.fhir_path?.[index]?.drls_apis?.[i]?.sub_type &&
                                              Boolean(
                                                (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type
                                              )
                                            }
                                          >
                                            <InputLabel>Subtype</InputLabel>
                                            <Select
                                              multiple
                                              name={`fhir_path[${index}].drls_apis[${i}].sub_type`}
                                              value={values.fhir_path[index].drls_apis[i].sub_type || []}
                                              onChange={(e) => {
                                                const value = e.target.value as string[];

                                                setFieldValue(
                                                  `fhir_path[${index}].drls_apis[${i}].sub_type`, value
                                                );
                                              }}
                                              label="Subtype"
                                              disabled={!selectedType}
                                              renderValue={(selected) => (selected as string[]).join(', ')}
                                            >
                                              {subTypeOptions.map((item: string) => (
                                                <MenuItem key={item} value={item}>
                                                  <Checkbox checked={values.fhir_path[index].drls_apis[i].sub_type.includes(item)} />
                                                  <ListItemText primary={item} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                            {touched.fhir_path?.[index]?.drls_apis?.[i]?.sub_type && Boolean(
                                              (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type
                                              ) && (
                                              <FormHelperText
                                                className={
                                                  Boolean(
                                                    (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type
                                                  ) &&
                                                  touched.fhir_path?.[index]?.drls_apis?.[i]?.sub_type &&
                                                  Boolean(
                                                    (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type
                                                  )
                                                    ? 'Mui-error'
                                                    : ''
                                                }
                                              >
                                                {(errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type &&
                                                  touched.fhir_path?.[index]?.drls_apis?.[i]?.sub_type &&
                                                  (errors.fhir_path?.[index] as any)?.drls_apis?.[i]?.sub_type
                                                }
                                              </FormHelperText>
                                            )}
                                          </FormControl>
                                        </Grid>
                                      </React.Fragment>
                                    )
                                  })}
                                </Grid>
                                {/* FHIR Path */}
                                <Grid item md={6} xs={12}>
                                  <TextField
                                    required={values?.fhir_path[index]?.drls_apis[0]?.type ? true : false}
                                    label="Path"
                                    name={`fhir_path[${index}].path`}
                                    value={
                                      typeof values.fhir_path[index].path === 'string'
                                        ? values.fhir_path[index].path
                                        : JSON.stringify(values.fhir_path[index].path, null, 2)
                                    }
                                    onChange={(e) => {
                                      setFieldValue(`fhir_path[${index}].path`, e.target.value);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={10}
                                    onBlur={(e) => {
                                      try {
                                        const formatted = JSON.stringify(JSON.parse(e.target.value), null, 2);
                                        setFieldValue(`fhir_path[${index}].path`, formatted);
                                      } catch {
                                        // invalid JSON — ignore or show error
                                      }
                                    }}
                                    variant="outlined"
                                    error={
                                      touched.fhir_path?.[index]?.path &&
                                      Boolean((errors.fhir_path?.[index] as any)?.path)
                                    }
                                    helperText={
                                      touched.fhir_path?.[index]?.path &&
                                      Boolean((errors.fhir_path?.[index] as any)?.path)
                                        ? (errors.fhir_path?.[index] as any)?.path
                                        : ''
                                    }
                                  />
                                </Grid>

                                {/* ACTION BUTTONS */}
                                <Grid item md={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {index === values.fhir_path.length - 1 ? (
                                    <Tooltip title="Add Row">
                                      <IconButton
                                        color="primary"
                                        aria-label="Add"
                                        onClick={() =>
                                          push({ drls_apis: [{
                                            type: '', sub_type: []
                                          }], path: '' })
                                        }
                                        disabled={values?.field_category?.includes('standard')}
                                      >
                                        <AddBoxIcon />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Delete Row">
                                      <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => remove(index)}
                                        disabled={values?.field_category?.includes('standard')}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Grid>
                              </Grid>                              
                            ))}
                          </>
                        )}
                      </FieldArray>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    justifyContent="center"
                    spacing={2}
                    className={styles.btns}
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setOpenAddPAConfig(false)}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    {!values?.field_category?.includes('standard') && (
                      <Grid item>
                        <Button variant="contained" color="primary" type="submit" disabled={submitLoader}>
                          {submitLoader && (
                            <CircularProgress
                              size={20}
                              style={{ marginRight: 5, color: '#fff' }}
                            />
                          )}
                          Save
                        </Button>
                      </Grid>
                    )}                    
                  </Grid>
                </DialogContent>
              </form>
            </Box>
          )
        }}
      </Formik>
    </Dialog>
  );
};

export default AddEditPAConfig